/**
 * Hour input step/precision, configurable on the Preferences page and stored
 * server-side as `preferences.weekActionsHourStep`. Ported from
 * script_plugin.user.js's HOUR_CFG / roundHours / formatHourString.
 */

export const HOUR_STEP_OPTIONS = ["1", "0.5", "0.25", "0.1", "0.01", "0.001"] as const;
export type HourStep = typeof HOUR_STEP_OPTIONS[number];

export const HOUR_STEP_DEFAULT: HourStep = "0.01";

// In-memory mirror of the backend-stored `preferences.weekActionsHourStep`, kept
// fresh by TimeTable.tsx whenever Redux preferences load/change. Replaces the old
// localStorage-backed store now that the hour step lives in user preferences.
let hourStepCache: HourStep = HOUR_STEP_DEFAULT;

export function isAllowedStep(step: string): step is HourStep {
    return (HOUR_STEP_OPTIONS as readonly string[]).includes(step);
}

export function decimalsFromStep(stepStr: string): number {
    const s = String(stepStr || "").trim();
    if (!s || !/^\d+(\.\d+)?$/.test(s)) { return 2; }
    const dot = s.indexOf(".");
    return dot === -1 ? 0 : (s.length - dot - 1);
}

export function getHourStep(): HourStep {
    return hourStepCache;
}

export function setHourStep(value: string): void {
    if (!isAllowedStep(value)) { return; }
    hourStepCache = value;
}

/** Called whenever Redux preferences load/change (see TimeTable.tsx). */
export function syncHourStepFromPreferences(value: string | undefined | null): void {
    hourStepCache = value && isAllowedStep(value) ? value : HOUR_STEP_DEFAULT;
}

export function getHourDecimals(): number {
    return decimalsFromStep(getHourStep());
}

/** Rounds a raw hour value to the configured step, cleaning floating-point noise. */
export function roundHours(n: number): number {
    const num = Number(n);
    if (!Number.isFinite(num)) { return 0; }
    const stepNum = Number(getHourStep());
    if (!Number.isFinite(stepNum) || stepNum <= 0) { return num; }
    const snapped = Math.round(num / stepNum) * stepNum;
    const d = getHourDecimals();
    const f = Math.pow(10, d);
    return Math.round(snapped * f) / f;
}

/** Reads a raw hour value from API data WITHOUT rounding, to preserve server precision. */
export function parseRawAmount(hours: unknown): number {
    const n = Number(hours);
    return Number.isFinite(n) ? n : 0;
}

export function formatHourString(n: number): string {
    const r = roundHours(n);
    const d = getHourDecimals();
    return d > 0 ? r.toFixed(d) : String(r);
}

/** Parses a user-entered hour string; returns null when invalid (empty = 0). */
export function parseEditableHour(valueRaw: string): number | null {
    const value = String(valueRaw || "").trim();
    if (!value) { return 0; }
    const n = Number(value);
    if (!Number.isFinite(n) || n < 0 || n > 24) { return null; }
    const rounded = roundHours(n);
    return (rounded < 0 || rounded > 24) ? null : rounded;
}
